import type { RecommendedAction } from '@/versions/v1/schema/case';
import type { PlantRoleId, RoleTable } from '@/versions/v1/schema/plant-role';
import type { SeriesCadence } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { RECOMMENDED_ACTIONS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import {
  EXTRA_ROLE_TABLES,
  LIME_PH_CIRCUIT_BARS,
  REAGENT_TANK_LEVELS,
  ROLE_TABLES,
} from '@/versions/v1/shared/bunker-hill-seed';
import { SERIES_BY_ID } from '@/versions/v1/shared/bunker-hill-series';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { CONCENTRATE_SPECS } from '@/versions/v1/shared/bunker-hill-concentrate-specs';
import { seriesAt, valueAt } from '@/versions/v1/shared/series.derive';
import type { VizTone } from '@/versions/v1/modules/_viz';
import { defaultTabForRole } from './Scene.transform';

export type ReagentStandingFinding = {
  deltaPct: number;
  sinceDays: number;
};

export const REAGENT_READING_BY_ROW: Record<string, string> = {
  'rg-1': 'rd-reag-znso4',
  'rg-2': 'rd-reag-smbs',
  'rg-3': 'rd-reag-cuso4',
  'rg-4': 'rd-reag-mibc',
  'rg-5': 'rd-reag-3418a',
  'rg-6': 'rd-reag-aero',
  'rg-7': 'rd-reag-lime',
};

const ALL_TABLES: readonly RoleTable[] = [...ROLE_TABLES, ...EXTRA_ROLE_TABLES];

export function roleTable(
  roleId: PlantRoleId,
  captionIncludes: string,
  tabId = defaultTabForRole(roleId),
): RoleTable | undefined {
  return ALL_TABLES.find(
    (table) =>
      table.roleId === roleId &&
      table.tabId === tabId &&
      table.caption.toLowerCase().includes(captionIncludes.toLowerCase()),
  );
}

export function equipmentStatusCards(roleId: PlantRoleId): RoleTable | undefined {
  return roleTable(roleId, 'area health');
}

export function recommendedActionsForCase(caseId: string): readonly RecommendedAction[] {
  return RECOMMENDED_ACTIONS.filter((action) => action.caseId === caseId);
}

export function reagentTankMeters() {
  return REAGENT_TANK_LEVELS;
}

function doseOutsideBand(actual: number, design: number): boolean {
  if (design <= 0) return false;
  const deltaPct = ((actual - design) / design) * 100;
  return Math.abs(deltaPct) > 5;
}

function cadencePointsPerDay(cadence: SeriesCadence): number {
  if (cadence === 'shift') return 2;
  if (cadence === 'daily') return 1;
  if (cadence === 'hourly') return 24;
  return 1;
}

function consecutiveOutsideDays(
  seriesId: string,
  design: number,
  state: SessionState,
): number {
  const view = seriesAt(seriesId, state);
  const series = SERIES_BY_ID[seriesId];
  const pointsPerDay = cadencePointsPerDay(series?.cadence ?? 'shift');
  let count = 0;
  for (let i = view.points.length - 1; i >= 0; i -= 1) {
    const point = view.points[i]?.value;
    if (point === null || point === undefined || !doseOutsideBand(point, design)) break;
    count += 1;
  }
  return Math.max(1, Math.floor(count / pointsPerDay));
}

export function reagentStandingFindings(state: SessionState): ReadonlyMap<string, ReagentStandingFinding> {
  const findings = new Map<string, ReagentStandingFinding>();

  for (const [rowId, readingId] of Object.entries(REAGENT_READING_BY_ROW)) {
    const reading = readingByIdLookup(readingId);
    if (!reading?.design || reading.value === undefined) continue;
    const design = reading.design;
    const actual = valueAt(readingId, state) ?? reading.value;
    const deltaPct = Math.round(((actual - design) / design) * 100);
    const seriesId = `rs-${readingId.replace('rd-', '')}`;
    const sinceDays = consecutiveOutsideDays(seriesId, design, state);
    if (sinceDays > 3 && doseOutsideBand(actual, design)) {
      findings.set(rowId, { deltaPct, sinceDays });
    }
  }

  return findings;
}

export function reagentStandingFindingLabel(finding: ReagentStandingFinding): string {
  const direction = finding.deltaPct < 0 ? 'under' : 'over';
  const dayWord = finding.sinceDays === 1 ? 'day' : 'days';
  return `${Math.abs(finding.deltaPct)} % ${direction} · ${finding.sinceDays} ${dayWord}`;
}

export function reagentTableWithDerivedDeltas(table: RoleTable, state: SessionState): RoleTable {
  const deltaColIdx = table.columns.findIndex((column) => column === 'Δ');
  if (deltaColIdx < 0) return table;

  return {
    ...table,
    rows: table.rows.map((row) => {
      const readingId = REAGENT_READING_BY_ROW[row.id];
      if (!readingId) return row;
      const reading = readingByIdLookup(readingId);
      if (!reading?.design) return row;
      const actual = valueAt(readingId, state) ?? reading.value;
      if (actual === undefined) return row;
      const deltaPct = Math.round(((actual - reading.design) / reading.design) * 100);
      const sign = deltaPct >= 0 ? '+' : '−';
      const cells = [...row.cells];
      cells[deltaColIdx] = `${sign}${Math.abs(deltaPct)}%`;
      return { ...row, cells };
    }),
  };
}

/** The CRO key-process-variable rows whose reading, setpoint and status are a
 *  `ConcentrateSpec` grade-spec verdict, keyed to the grade spec's own joined
 *  `Reading`. `cro-r11` printed deck slide 12's untraced `Spec >25` floor
 *  beside a typed `WARN` while the Ag content met the spec (#730). */
const CRO_KPV_GRADE_SPEC_READING: Record<string, string> = {
  'cro-r11': 'rd-spec-ag-grade',
  // polish.P15 adjudication 7: one Zn concentrate spec, the ConcentrateSpec's
  // `> 55 % Zn` (as the Senior metallurgist's landing draws it), not the
  // deck row's `Spec >51`.
  'cro-r12': 'rd-spec-zn-grade',
};

/**
 * One concentrate grade-spec verdict as the table's three data cells — the
 * reading at the grade spec's own precision and unit, its setpoint (the
 * spec's `lo` floor where it carries one, else the reading's design basis,
 * the comparator `ConcentrateQualityPanel` already reads for Ag content),
 * and `OK` / `WARN` from the value against that comparator. Returns null
 * where the spec or its reading cannot answer, so the seed row stands.
 */
function gradeSpecVerdictCells(readingId: string, state: SessionState): string[] | null {
  const reading = readingByIdLookup(readingId);
  if (!reading) return null;
  const gradeSpec = CONCENTRATE_SPECS.flatMap((spec) => spec.gradeSpecs).find(
    (row) => row.readingId === readingId,
  );
  const actual = valueAt(readingId, state) ?? reading.value;
  const target = gradeSpec?.lo ?? reading.design;
  if (actual === undefined || target === undefined) return null;
  const decimals = gradeSpec?.displayDecimals ?? reading.decimals ?? 1;
  const unit = gradeSpec?.unit ?? reading.unit;
  const inSpec = (reading.higherIsBetter ?? true) ? actual >= target : actual <= target;
  // A spec floor prints at its own precision (`Spec >55`), a design basis at the reading's.
  const targetText =
    gradeSpec?.lo !== undefined
      ? `Spec >${Number.isInteger(gradeSpec.lo) ? String(gradeSpec.lo) : gradeSpec.lo.toFixed(decimals)}`
      : `Design ${target.toFixed(decimals)}`;
  // The table's own format: a `%` unit rides its figure (`58.4% Zn`, beside
  // `72.1%`), every other unit keeps a space (`12.14 oz/t`) (item 6).
  const unitSep = unit.startsWith('%') ? '' : ' ';
  return [
    `${actual.toFixed(decimals)}${unitSep}${unit}`,
    `${targetText}${unitSep}${unit}`,
    inSpec ? 'OK' : 'WARN',
  ];
}

/** The CRO key-process-variable table with each grade-spec row's reading,
 *  setpoint and status derived from the `ConcentrateSpec` it names (#730),
 *  so a value at its spec reads quiet and only an exception takes colour. */
export function croKpvTable(table: RoleTable, state: SessionState): RoleTable {
  return {
    ...table,
    rows: table.rows.map((row) => {
      const readingId = CRO_KPV_GRADE_SPEC_READING[row.id];
      if (!readingId) return row;
      const cells = gradeSpecVerdictCells(readingId, state);
      return cells ? { ...row, cells: [row.cells[0] ?? '', ...cells] } : row;
    }),
  };
}

export function limePhMeters() {
  return LIME_PH_CIRCUIT_BARS;
}

export function triadColumnClass(column: string): string | undefined {
  const key = column.toLowerCase();
  if (key === 'design') return 'text-[var(--ink-tertiary)]';
  if (key.includes('budget target') || key === 'target') return 'text-[var(--state-warning)]';
  if (key.includes('actual')) return 'text-[var(--ink-primary)] font-medium';
  return undefined;
}

export function deltaCellClass(value: string): string | undefined {
  if (value.startsWith('−') || value.startsWith('-')) {
    return 'text-[var(--state-blocked)]';
  }
  if (value.startsWith('+')) {
    return 'text-[var(--state-online)]';
  }
  return undefined;
}

export function reagentDeltaClass(value: string): string | undefined {
  if (value.startsWith('−') || value.startsWith('-')) {
    return 'text-[var(--state-online)]';
  }
  if (value.startsWith('+')) {
    return 'text-[var(--state-warning)]';
  }
  return undefined;
}

export function healthMeterTone(pct: number): VizTone {
  if (pct >= 80) return 'ok';
  if (pct >= 65) return 'warn';
  return 'bad';
}

export function parseHealthPct(value: string): number {
  return Number.parseInt(value.replace('%', ''), 10);
}

export function isSyntheticMaintenanceKpi(kpiId: string): boolean {
  return kpiId === 'mnt-mtbf' || kpiId === 'mnt-mttr';
}
